import { msg } from '@/plugins/message'

/**
 * 与 public/authen.js 中 JIT_AUTHEN 的 P1 本地控件逻辑保持 1:1 一致。
 * 仅将 jQuery 后端请求拆到 jitAuth.ts，控件侧提示改用平台 message 组件。
 */

const JIT_INIT_PARAM =
  '<?xml version="1.0" encoding="utf-8"?>' +
  '<authinfo><liblist>' +
  '<lib type="CSP" version="1.0" dllname="" ><algid val="SHA1" sm2_hashalg="sm3"/></lib>' +
  '<lib type="SKF" version="1.1" dllname="U0tGQVBJMjA1MjkuZGxs" ><algid val="SHA1" sm2_hashalg="sm3"/></lib>' +
  '<lib type="SKF" version="1.2" dllname="SklUU0tGLmRsbA==" ><algid val="SHA1" sm2_hashalg="sm3"/></lib>' +
  '</liblist><checkkeytimes><item times="1" ></item></checkkeytimes></authinfo>'

export interface JitCertOption {
  sn: string
  dn: string
}

export interface JitP1SignResult {
  result?: number
  remainretrycount?: number
  signdata?: string
  cert?: string
  algid?: string
  sn?: string
  certsn?: string
  certSn?: string
}

/** 登录/绑定时用的序列号：优先控件回包，其次下拉框选中值 */
export function pickClientCertSn(
  signObj: JitP1SignResult | '' | null | undefined,
  selectedSn?: string,
): string {
  if (signObj && typeof signObj === 'object') {
    const fromSign = [signObj.certSn, signObj.certsn, signObj.sn]
      .map((value) => String(value || '').trim())
      .find(Boolean)
    if (fromSign) return fromSign
  }
  return String(selectedSn || '').trim()
}

interface JitGwExtInterface {
  GetVersion(): string
  ClearFilter(): void
  Initialize(appName: string, initParam: string): void
  Finalize(): void
  GetCertList(): string
  P1SignWithPin(sn: string, pin: string, random: string): string
  GetLastError(): number
  GetLastErrorMessage(): string
}

declare global {
  interface Window {
    JIT_GW_ExtInterface?: JitGwExtInterface
  }
}

let pnxScriptLoading: Promise<void> | null = null

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existed = document.querySelector<HTMLScriptElement>(`script[data-jit-src="${src}"]`)
    if (existed) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.jitSrc = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`加载脚本失败: ${src}`))
    document.head.appendChild(script)
  })
}

async function getJitGwExtInterface(): Promise<JitGwExtInterface> {
  if (!pnxScriptLoading) {
    pnxScriptLoading = loadScript('/pnxclient.js')
  }
  await pnxScriptLoading
  const client = window.JIT_GW_ExtInterface
  if (!client) {
    throw new Error('未检测到 PNX 控件接口')
  }
  return client
}

function handleGetLastError(client: JitGwExtInterface): void {
  if (client.GetLastError() === 0) return
  const message = client.GetLastErrorMessage()?.trim()
  if (!message || message === '操作成功') return
  if (
    client.GetLastError() === 3758096386 ||
    client.GetLastError() === 2148532334 ||
    client.GetLastError() === 3758096385
  ) {
    msg.warning('用户取消操作')
    return
  }
  if (client.GetLastError() === -536870815 || client.GetLastError() === 3758096481) {
    msg.warning('没有找到有效的证书，如果使用的是 KEY，请确认已经插入')
    return
  }
  msg.error(client.GetLastErrorMessage())
}

/** 对应 authen.js: getCertList */
export async function loadJitCertList(): Promise<JitCertOption[]> {
  const client = await getJitGwExtInterface()
  client.Initialize('', JIT_INIT_PARAM)
  const cert = client.GetCertList()
  const arr = cert.split('\r\n').filter((line) => line.trim() !== '')
  const options: JitCertOption[] = []
  for (const ele of arr) {
    const n = ele.lastIndexOf(' ')
    if (n < 0) continue
    const dn = ele.substring(0, n)
    const sn = ele.substring(n + 1)
    options.push({ sn, dn })
  }
  client.Finalize()
  return options
}

/** 对应 authen.js: AuthP1Sign_certSelect */
export async function signJitP1WithPin(
  random: string,
  certSn: string,
  pin: string,
): Promise<JitP1SignResult | ''> {
  const client = await getJitGwExtInterface()
  let signResultObj: JitP1SignResult | '' = ''

  try {
    client.GetVersion()
  } catch {
    msg.error('未安装 PNX 客户端控件，请先安装后再试')
    return ''
  }

  try {
    client.ClearFilter()
    client.Initialize('', JIT_INIT_PARAM)
  } catch {
    msg.error(`调用方法失败：${client.GetLastErrorMessage()}`)
  }

  try {
    const signobj = client.P1SignWithPin(certSn, pin, random)
    signResultObj = JSON.parse(signobj) as JitP1SignResult
    if (signResultObj.result != 0) {
      if ((signResultObj.remainretrycount ?? -1) < 0) {
        msg.error(client.GetLastErrorMessage())
      } else {
        msg.warning(`PIN 码不正确，还剩余 ${signResultObj.remainretrycount} 次重试机会`)
      }
      handleGetLastError(client)
    }
  } catch {
    msg.error(`生成签名信息失败：${client.GetLastErrorMessage()}`)
    handleGetLastError(client)
  }

  client.Finalize()
  return signResultObj
}
